<template>
  <div class="assetContainer" :class="{ withVideo: assetType === 'video', assetLoaded }">
    <div v-if="assetType === 'image'" ref="imageContainer" />

    <video
      v-if="assetType === 'video'"
      ref="videoPlayer"
      class="asset video no-select"
      @loadeddata="onAssetLoaded"
      @ended="onVideoEnded"
      @error="onVideoError"
      muted
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import type { Ref } from 'vue'
import type { AssetType } from '@/customTypes'

// Store
import { useDataStore } from '@/stores/DataStore'
import { usePreloadStore } from '@/stores/PreloadStore'

// Helpers
import { useTimeout } from '@/utils/helpers'
const { setManagedTimeout } = useTimeout()

import Logger from '@/services/Logger.js'

const logger: any = new Logger('AssetComponent', true)

const props = defineProps({
  assetUrl: {
    type: String,
    required: true
  },
  assetType: {
    type: String as () => AssetType,
    required: true
  },
  transitionDuration: {
    type: Number,
    default: 1000
  },
  playVideo: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  (e: 'video-ended'): void
}>()

const dataStore = useDataStore()
const { screenerTemplateId, isOffline } = storeToRefs(dataStore)
const preloadStore = usePreloadStore()
const { preloadedImages } = storeToRefs(preloadStore)

const videoPlayer: Ref<HTMLVideoElement | null> = ref(null)
const assetLoaded: Ref<boolean> = ref(false)
const imageContainer: Ref<HTMLDivElement | null> = ref(null)

const assetLoadingStates: Ref<'loading' | 'loaded' | 'error'> = ref('loading')
const maxRetries = 3
const retryDelay = 5
let assetErrorId: string | null = null

const videoFormats = [
  { type: 'webm', mimeType: 'video/webm' },
  { type: 'mp4', mimeType: 'video/mp4' }
]

watch(isOffline, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    // Network is back online
    if (assetLoadingStates.value === 'error') {
      retryLoadAsset()
    }
  }
})

onMounted(() => {
  loadAsset()
})

// Delete asset after use
onBeforeUnmount(() => {
  if (props.assetType === 'image') {
    while (imageContainer.value?.firstChild) {
      imageContainer.value?.removeChild(imageContainer.value?.firstChild)
    }
    preloadStore.deleteImageFromCache(props.assetUrl)
  }
  if (assetErrorId) {
    dataStore.clearLoadErrorTimeout(assetErrorId)
  }
})

watch(
  () => props.playVideo,
  (newVal) => {
    if (props.assetType !== 'video') return
    logger.info('playVideo', newVal)

    if (videoPlayer.value) {
      if (newVal) {
        videoPlayer.value.play()
      } else {
        videoPlayer.value.pause()
      }
    }
  }
)

const asset = computed(
  () =>
    `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/question-assets/${props.assetUrl}?v=${_ASSETS_VERSION_}`
)

const fallbackAsset = computed(
  () =>
    `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/question-assets/${props.assetUrl.replace('.webm', '.mp4')}?v=${_ASSETS_VERSION_}`
)

function onAssetLoaded() {
  assetLoaded.value = true
  assetLoadingStates.value = 'loaded'
  if (assetErrorId) {
    dataStore.clearLoadErrorTimeout(assetErrorId)
  }

  setManagedTimeout(() => {
    if (videoPlayer.value && props.playVideo) videoPlayer.value.play()
  }, props.transitionDuration)
}

const retryLoadAsset = () => {
  assetLoadingStates.value = 'loading'
  loadAsset()
}

const loadAsset = async (retryCount = 0): Promise<void> => {
  if (retryCount >= maxRetries) {
    assetLoadingStates.value = 'error'
    return
  }

  try {
    if (props.assetType === 'image') {
      loadImage(retryCount)
    } else if (props.assetType === 'video') {
      await loadVideo()
    }
  } catch (error) {
    logger.error(`Error loading asset ${asset.value}:`, error)
    assetLoadingStates.value = 'error'
  }
}

const loadImage = (retryCount: number) => {
  const preloadedImage = preloadedImages.value[props.assetUrl]
  if (preloadedImage) {
    imageContainer.value?.appendChild(preloadedImage)
    assetLoaded.value = true
    const img = imageContainer.value?.querySelector('img')
    if (img) img.classList.add('asset')
    onAssetLoaded()
  } else {
    const img = new Image()
    img.src = asset.value
    imageContainer.value?.appendChild(img)
    img.onload = onAssetLoaded
    img.onerror = (event) => handleAssetError(retryCount, event as Event)
    img.classList.add('asset')
  }
  preloadStore.loadNextQuestionAssets()
}

const loadVideo = async () => {
  if (!videoPlayer.value) return

  for (const format of videoFormats) {
    const canPlay = videoPlayer.value.canPlayType(format.mimeType)
    if (canPlay === '') continue

    const videoUrl = format.type === 'webm' ? asset.value : fallbackAsset.value
    try {
      await loadVideoSource(videoUrl)
      logger.info(`Successfully loaded ${format.type} video`)
      return
    } catch (error) {
      logger.warn(`Failed to load ${format.type} video:`, error)
    }
  }

  // If we've reached here, all formats failed
  logger.error('All video formats failed to load')
  handleAssetError(0, new Event('error'))
}

const loadVideoSource = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!videoPlayer.value) return reject('Video player not available')

    videoPlayer.value.src = src
    videoPlayer.value.load()

    const onLoadedData = () => {
      videoPlayer.value?.removeEventListener('loadeddata', onLoadedData)
      videoPlayer.value?.removeEventListener('error', onError)
      resolve()
    }

    const onError = (e: Event) => {
      videoPlayer.value?.removeEventListener('loadeddata', onLoadedData)
      videoPlayer.value?.removeEventListener('error', onError)
      reject(e)
    }

    videoPlayer.value.addEventListener('loadeddata', onLoadedData)
    videoPlayer.value.addEventListener('error', onError)
  })
}

const handleAssetError = (retryCount: number, event: Event) => {
  if (!isOffline.value) {
    setManagedTimeout(() => loadAsset(retryCount + 1), retryDelay * 1000 * retryCount)
  } else {
    assetLoadingStates.value = 'error'
  }
  assetErrorId = dataStore.startLoadErrorTimeout(event)
}

function onVideoEnded() {
  emit('video-ended')
}

function onVideoError(event: Event) {
  logger.error('Video error:', event)
  handleAssetError(0, event)
}
</script>

<style lang="scss">
.assetContainer {
  position: fixed;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-height: calc(var(--full-vh) * 0.5);
  width: calc(var(--full-vw) * 0.3);
  opacity: 0;

  &.withVideo {
    width: calc(var(--full-vw) * 0.55);
  }

  &.assetLoaded {
    opacity: 1;
    transition: opacity v-bind('`${transitionDuration}ms`');
  }

  .asset {
    width: 100%;
    height: 100%;
    object-fit: cover;
    box-shadow: 6px 5px 11px 2px rgba(0, 0, 0, 0.4);
    &.video {
      background-color: rgba(255, 255, 255, 0);
    }
  }
}
</style>
